/**
 * The Logiscool trainer accounts, in words (24.09.2026). Pure, so tested.
 * A licence is annual and belongs to the account: an account is a seat, lent
 * to one formator at a time, carrying as many cohorts as they teach.
 */

export function accountState(account) {
  const created = account?.created_in_limo ?? Boolean(account?.limo_trainer_id);
  const noLogin = account?.no_username ?? account?.limo_no_username;
  if (!created) {
    return { key: "not_created", label: "necreat în Logiscool", cls: "bg-[#FDF3E7] text-[#8a6a1a]" };
  }
  if (noLogin) {
    return { key: "no_login", label: "fără utilizator la Logiscool", cls: "bg-[#FDECEC] text-[#9b1f2c]" };
  }
  if (account.holder) {
    return { key: "held", label: "în folosință", cls: "bg-[#EAF4EE] text-[#2F6B4E]" };
  }
  return { key: "free", label: "liber", cls: "bg-[#EDEEF0] text-[#373A45]" };
}

/**
 * Who this account can go to, and what to say about each one.
 *
 * A formator holds at most one account, so somebody who already has another
 * seat is listed but cannot be picked — the backend refuses it anyway, and a
 * refusal after the click explains far less than a line next to the name.
 * The current holder is not an option either: handing a seat to the person
 * who already has it is not a handover.
 */
export function handoverOptions(account, trainers = [], accounts = []) {
  const elsewhere = new Map();
  accounts.forEach((a) => {
    if (a?.holder?.trainer_id && a.id !== account?.id) elsewhere.set(a.holder.trainer_id, a.label);
  });
  return trainers
    .filter((t) => t.id !== account?.holder?.trainer_id)
    .map((t) => {
      const other = elsewhere.get(t.id);
      return { id: t.id, name: t.name, disabled: Boolean(other), note: other ? `ține deja ${other}` : "" };
    });
}

/**
 * What a handover does, in one sentence — above the picker. A cohort that
 * chose this account stays on it, with the new formator; one that made no
 * choice follows the formator who had it (19.10.2026: the note said every
 * cohort stays, which only the first kind does).
 */
export function handoverNote(account) {
  const all = account?.groups || [];
  const chosen = all.filter((g) => g.limo_account_id === account?.id).length;
  const following = account?.holder ? all.length - chosen : 0;
  let carried = "";
  if (chosen) carried += chosen === 1 ? " Grupa aleasă pe acest cont rămâne pe el."
    : ` Cele ${chosen} grupe alese pe acest cont rămân pe el.`;
  if (following) carried += following === 1 ? " O grupă fără cont ales merge cu formatorul actual."
    : ` Cele ${following} grupe fără cont ales merg cu formatorul actual.`;
  if (account?.holder) {
    return `Contul „${account.label}” trece de la ${account.holder.name} la formatorul ales.${carried}`;
  }
  return `Contul „${account?.label}” e liber și merge la formatorul ales.${carried}`;
}

/** How many seats are free — the number that says whether a new formator can start. */
export function freeSeats(accounts = []) {
  return accounts.filter((a) => !a.holder).length;
}

/** The one line above the table: how many seats there are, and how many are free. */
export function inventorySummary(data) {
  if (!data) return "";
  const { total = 0, held = 0, free = 0, trainer_pool: pool } = data;
  const seats = `${total} ${total === 1 ? "cont" : "conturi"} · ${held} în folosință · ${free} ${free === 1 ? "liber" : "libere"}`;
  if (!pool || typeof pool.assigned !== "number") return seats;
  const line = `${seats}. La Logiscool: ${pool.assigned} licențe de trainer consumate`;
  return typeof pool.free === "number" ? `${line}, ${pool.free} libere.` : `${line}.`;
}

/**
 * The username we propose for a new account, from its alias address.
 *
 * Mirrors the backend's own suggestion, so what the field shows is what gets
 * sent. Logiscool requires it and it must be unique across all of Logiscool —
 * if it is taken, they create the account WITHOUT a login and say nothing, so
 * the person who types it has to be able to see and change it.
 */
export function suggestUsername(email) {
  const raw = String(email || "").split("@")[0];
  return raw
    .toLowerCase()
    .replace(/[șş]/g, "s")
    .replace(/[țţ]/g, "t")
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9._-]+/g, ".")
    .replace(/\.{2,}/g, ".")
    .replace(/^[._-]+|[._-]+$/g, "")
    .slice(0, 40);
}
